import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CodesTab } from './codes-tab';
import { PromoCodeEditor, type EditorTarget } from './promo-code-editor';
import { RedemptionsDialog } from './redemptions-dialog';
import type { CodeKind } from './promo-codes.api';
import type { PromoCode } from './types';

/**
 * Promo codes — two tabs over one table.
 *
 * A code with no `userId` is a store code anyone can type at checkout; a code
 * with one belongs to a named customer. The API splits them across two search
 * routes, and the tabs follow that split rather than inventing a third view.
 *
 * "New code" creates whichever kind the open tab shows, so the button never has
 * to ask a question the tab already answered.
 */
export function PromoCodesPage() {
  const { t } = useTranslation();

  const [kind, setKind] = useState<CodeKind>('store');
  const [editing, setEditing] = useState<EditorTarget | null>(null);
  const [redemptionsFor, setRedemptionsFor] = useState<PromoCode | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('promoCodes.title')}
        description={t('promoCodes.subtitle')}
        actions={
          <Button
            size="sm"
            onClick={() => setEditing({ kind, existing: null })}
          >
            <Plus />
            <span className="hidden sm:inline">
              {kind === 'store'
                ? t('promoCodes.newStoreCode')
                : t('promoCodes.newCustomerCode')}
            </span>
          </Button>
        }
      />

      <Tabs value={kind} onValueChange={(value) => setKind(value as CodeKind)}>
        <TabsList>
          <TabsTrigger value="store">{t('promoCodes.tabStore')}</TabsTrigger>
          <TabsTrigger value="customer">
            {t('promoCodes.tabCustomer')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="store">
          <CodesTab
            kind="store"
            onEdit={(code) => setEditing({ kind: 'store', existing: code })}
            onViewRedemptions={setRedemptionsFor}
          />
        </TabsContent>
        <TabsContent value="customer">
          <CodesTab
            kind="customer"
            onEdit={(code) => setEditing({ kind: 'customer', existing: code })}
            onViewRedemptions={setRedemptionsFor}
          />
        </TabsContent>
      </Tabs>

      <PromoCodeEditor
        target={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />

      <RedemptionsDialog
        code={redemptionsFor}
        open={redemptionsFor !== null}
        onOpenChange={(next) => {
          if (!next) setRedemptionsFor(null);
        }}
      />
    </div>
  );
}
